<template>
  <section class="signin-page">
    <div class="signin-shell">
      <aside class="signin-brand">
        <div class="brand-copy">
          <p class="eyebrow">AI voice receptionist</p>
          <h1>Answer every call, capture every lead, book every appointment.</h1>
          <p class="lead">
            Start with 10 free calls. Then keep your AI receptionist live for
            100 calls for $250. Collect partial or full payments
            by phone, add a chatbot to an existing website, or launch a simple
            website quickly if the business does not have one yet.
          </p>
        </div>

        <div class="proof-grid" aria-label="NoCodeAgent benefits">
          <div class="proof-item">
            <PhoneCall :size="20" stroke-width="2.2" />
            <span>Handles missed calls</span>
          </div>
          <div class="proof-item">
            <CalendarCheck :size="20" stroke-width="2.2" />
            <span>Books appointments</span>
          </div>
          <div class="proof-item">
            <MessageSquareText :size="20" stroke-width="2.2" />
            <span>Follows up by SMS</span>
          </div>
          <div class="proof-item">
            <CreditCard :size="20" stroke-width="2.2" />
            <span>Takes deposits by phone</span>
          </div>
          <div class="proof-item">
            <MessagesSquare :size="20" stroke-width="2.2" />
            <span>Embeds as a website chatbot</span>
          </div>
          <div class="proof-item">
            <Globe2 :size="20" stroke-width="2.2" />
            <span>Creates a website fast</span>
          </div>
        </div>

        <div class="call-card">
          <div class="call-card-header">
            <span class="pulse"></span>
            <strong>Live call flow</strong>
          </div>
          <div class="call-line">
            <span>Caller</span>
            <p>I need an appointment tomorrow afternoon.</p>
          </div>
          <div class="call-line ai">
            <span>AI receptionist</span>
            <p>I can help with that. Which service do you need?</p>
          </div>
        </div>
      </aside>

      <main class="signin-panel" aria-labelledby="signin-title">
        <div class="panel-header">
          <p class="panel-kicker">Welcome back</p>
          <h2 id="signin-title">Sign in to your workspace</h2>
          <p>Manage your agents, test live calls, and review bookings.</p>
        </div>

        <div class="social-login">
          <button class="social-btn google" type="button" @click="loginWithGoogle">
            <svg class="social-logo" viewBox="0 0 24 24" aria-hidden="true">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.24 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
              <path fill="#FBBC05" d="M5.84 14.1c-.22-.66-.35-1.36-.35-2.1s.13-1.44.35-2.1V7.06H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.94l3.66-2.84z" />
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.06L5.84 9.9C6.71 7.3 9.14 5.38 12 5.38z" />
            </svg>
            Continue with Google
          </button>

          <button class="social-btn facebook" type="button" @click="loginWithFacebook">
            <svg class="social-logo" viewBox="0 0 24 24" aria-hidden="true">
              <path fill="#1877F2" d="M24 12.07C24 5.4 18.63 0 12 0S0 5.4 0 12.07C0 18.1 4.39 23.1 10.13 24v-8.44H7.08v-3.49h3.05V9.41c0-3.02 1.79-4.69 4.53-4.69 1.31 0 2.68.24 2.68.24v2.96h-1.51c-1.49 0-1.96.93-1.96 1.89v2.26h3.33l-.53 3.49h-2.8V24C19.61 23.1 24 18.1 24 12.07z" />
            </svg>
            Continue with Facebook
          </button>
        </div>

        <div class="divider">
          <span>or sign in with email</span>
        </div>

        <form class="signin-form" @submit.prevent="submitLogin">
          <label for="email">Email</label>
          <div class="field">
            <Mail :size="18" stroke-width="2.2" />
            <input
              id="email"
              v-model.trim="form.email"
              type="email"
              autocomplete="email"
              placeholder="you@company.com"
              required
            />
          </div>

          <div class="label-row">
            <label for="password">Password</label>
            <router-link to="/forgot-password">Forgot password?</router-link>
          </div>
          <div class="field">
            <LockKeyhole :size="18" stroke-width="2.2" />
            <input
              id="password"
              v-model="form.password"
              type="password"
              autocomplete="current-password"
              placeholder="Enter your password"
              required
            />
          </div>

          <p v-if="error" class="error-message" role="alert">{{ error }}</p>

          <button class="submit-btn" type="submit" :disabled="loading">
            <span>{{ loading ? 'Signing in...' : 'Sign in' }}</span>
            <ArrowRight :size="18" stroke-width="2.2" />
          </button>
        </form>

        <p class="signup-link">
          New to NoCodeAgent?
          <router-link to="/signup">Try 10 calls free</router-link>
        </p>
      </main>
    </div>
  </section>
</template>

<script setup>
import { computed, reactive, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import {
  ArrowRight,
  CalendarCheck,
  CreditCard,
  Globe2,
  LockKeyhole,
  Mail,
  MessageSquareText,
  MessagesSquare,
  PhoneCall
} from 'lucide-vue-next'
import Cookies from 'js-cookie'
import * as api from '@/services/api.js'

const route = useRoute()
const router = useRouter()

const loading = ref(false)
const error = ref('')

const form = reactive({
  email: '',
  password: ''
})

const nextUrl = computed(() => {
  return route.query.next ? String(route.query.next) : '/app/dashboard'
})

function loginWithGoogle() {
  window.location.href = 'https://nocodeagent.ai/api/auth/google'
}

function loginWithFacebook() {
  window.location.href = 'https://nocodeagent.ai/api/auth/facebook'
}

async function submitLogin() {
  try {
    loading.value = true
    error.value = ''

    const response = await api.auth.login({
      email: form.email,
      password: form.password
    })

    if (!response?.token) {
      error.value = 'Login failed. Please check your credentials.'
      return
    }

    Cookies.set('token', response.token, {
      secure: true,
      sameSite: 'Strict'
    })

    if (response.user?.name) localStorage.setItem('userName', response.user.name)
    if (response.user?.email) localStorage.setItem('userEmail', response.user.email)

    await router.push(nextUrl.value)
  } catch (err) {
    console.error('Login error:', err)
    error.value = 'Unable to sign in. Please check your email and password.'
  } finally {
    loading.value = false
  }
}
</script>

<style scoped>
.signin-page {
  min-height: calc(100vh - 144px);
  display: flex;
  align-items: center;
  justify-content: center;
  background:
    radial-gradient(circle at top left, rgba(16, 185, 129, 0.16), transparent 34%),
    linear-gradient(135deg, #fbfaf7 0%, #eef7f1 44%, #f6f0e8 100%);
  padding: 32px 20px;
  color: #17211b;
}

.signin-shell {
  width: min(1120px, 100%);
  display: grid;
  grid-template-columns: minmax(0, 1.05fr) minmax(400px, 0.95fr);
  background: rgba(255, 255, 255, 0.78);
  border: 1px solid rgba(23, 33, 27, 0.08);
  border-radius: 18px;
  box-shadow: 0 24px 70px rgba(34, 45, 38, 0.16);
  overflow: hidden;
}

.signin-brand {
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 34px;
  min-height: 680px;
  padding: 42px;
  background:
    linear-gradient(150deg, rgba(24, 91, 74, 0.96), rgba(25, 39, 33, 0.98)),
    #1f3f35;
  color: #ffffff;
}

.brand-copy {
  margin-top: auto;
  max-width: 560px;
}

.eyebrow,
.panel-kicker {
  margin: 0 0 12px;
  font-size: 0.76rem;
  font-weight: 800;
  letter-spacing: 0;
  text-transform: uppercase;
}

.eyebrow {
  color: #b8f5d6;
}

.brand-copy h1 {
  margin: 0;
  max-width: 680px;
  font-size: clamp(2.35rem, 4vw, 4.6rem);
  line-height: 0.98;
  letter-spacing: 0;
}

.lead {
  max-width: 520px;
  margin: 22px 0 0;
  color: rgba(255, 255, 255, 0.78);
  font-size: 1.05rem;
  line-height: 1.7;
}

.proof-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
}

.proof-item {
  display: flex;
  align-items: center;
  gap: 10px;
  min-height: 58px;
  padding: 13px;
  border: 1px solid rgba(255, 255, 255, 0.16);
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.08);
  color: rgba(255, 255, 255, 0.9);
  font-size: 0.88rem;
  font-weight: 700;
}

.proof-item svg {
  flex: 0 0 auto;
  color: #8ff0bd;
}

.call-card {
  margin-top: auto;
  max-width: 510px;
  padding: 18px;
  border: 1px solid rgba(255, 255, 255, 0.16);
  border-radius: 8px;
  background: rgba(9, 19, 16, 0.44);
}

.call-card-header {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 14px;
  font-size: 0.9rem;
}

.pulse {
  width: 9px;
  height: 9px;
  border-radius: 50%;
  background: #34d399;
  box-shadow: 0 0 0 6px rgba(52, 211, 153, 0.16);
}

.call-line {
  padding: 12px;
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.08);
}

.call-line + .call-line {
  margin-top: 10px;
}

.call-line span {
  display: block;
  margin-bottom: 6px;
  color: rgba(255, 255, 255, 0.58);
  font-size: 0.75rem;
  font-weight: 800;
}

.call-line p {
  margin: 0;
  color: rgba(255, 255, 255, 0.92);
  line-height: 1.45;
}

.call-line.ai {
  background: rgba(143, 240, 189, 0.12);
}

.signin-panel {
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding: 56px;
  background: #ffffff;
}

.panel-header {
  margin-bottom: 28px;
}

.panel-kicker {
  color: #0d8f62;
}

.panel-header h2 {
  margin: 0;
  color: #17211b;
  font-size: 2rem;
  line-height: 1.12;
}

.panel-header p {
  margin: 10px 0 0;
  color: #66736b;
  line-height: 1.55;
}

.social-login {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
}

.social-btn,
.submit-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  min-height: 48px;
  border-radius: 8px;
  border: 1px solid transparent;
  font-weight: 800;
  cursor: pointer;
  transition: transform 0.18s ease, box-shadow 0.18s ease, background 0.18s ease;
}

.social-logo {
  width: 18px;
  height: 18px;
  flex: 0 0 auto;
}

.social-btn:hover,
.submit-btn:hover:not(:disabled) {
  transform: translateY(-1px);
}

.google {
  background: #17211b;
  color: #ffffff;
}

.facebook {
  background: #ffffff;
  color: #17211b;
  border-color: #dde5df;
}

.divider {
  position: relative;
  margin: 28px 0;
  text-align: center;
}

.divider::before {
  content: '';
  position: absolute;
  inset: 50% 0 auto;
  height: 1px;
  background: #e6ece8;
}

.divider span {
  position: relative;
  z-index: 1;
  padding: 0 12px;
  background: #ffffff;
  color: #7a8780;
  font-size: 0.84rem;
  font-weight: 700;
}

.signin-form {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.signin-form label,
.label-row a {
  font-size: 0.9rem;
  font-weight: 800;
}

.signin-form label {
  color: #29352f;
}

.label-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  margin-top: 8px;
}

.label-row a {
  color: #0d8f62;
  text-decoration: none;
}

.label-row a:hover,
.signup-link a:hover {
  text-decoration: underline;
}

.field {
  display: flex;
  align-items: center;
  gap: 10px;
  min-height: 50px;
  padding: 0 14px;
  border: 1px solid #dce5df;
  border-radius: 8px;
  background: #fbfdfb;
  transition: border-color 0.18s ease, box-shadow 0.18s ease;
}

.field:focus-within {
  border-color: #18a16f;
  box-shadow: 0 0 0 4px rgba(24, 161, 111, 0.12);
}

.field svg {
  flex: 0 0 auto;
  color: #6b7a71;
}

.field input {
  width: 100%;
  min-width: 0;
  border: none;
  outline: none;
  background: transparent;
  color: #17211b;
  font: inherit;
}

.field input::placeholder {
  color: #9aa6a0;
}

.error-message {
  margin: 6px 0 0;
  padding: 12px 14px;
  border: 1px solid rgba(185, 28, 28, 0.18);
  border-radius: 8px;
  background: rgba(239, 68, 68, 0.08);
  color: #b91c1c;
  font-size: 0.9rem;
  line-height: 1.4;
}

.submit-btn {
  width: 100%;
  margin-top: 12px;
  background: #0d8f62;
  color: #ffffff;
  box-shadow: 0 14px 30px rgba(13, 143, 98, 0.2);
}

.submit-btn:disabled {
  cursor: not-allowed;
  opacity: 0.68;
  box-shadow: none;
}

.signup-link {
  margin: 22px 0 0;
  color: #66736b;
  text-align: center;
}

.signup-link a {
  color: #0d8f62;
  font-weight: 800;
  text-decoration: none;
}

@media (max-width: 980px) {
  .signin-shell {
    grid-template-columns: 1fr;
  }

  .signin-brand {
    min-height: auto;
    padding: 32px;
  }

  .brand-copy {
    margin-top: 34px;
  }
}

@media (max-width: 640px) {
  .signin-page {
    min-height: calc(100vh - 56px);
    align-items: flex-start;
    padding: 0;
    background: #ffffff;
  }

  .signin-shell {
    min-height: auto;
    border: none;
    border-radius: 0;
    box-shadow: none;
  }

  .signin-brand {
    display: none;
  }

  .signin-panel {
    min-height: calc(100vh - 56px);
    justify-content: flex-start;
    padding: 22px 20px 28px;
  }

  .panel-header {
    margin-bottom: 18px;
  }

  .panel-kicker {
    margin-bottom: 8px;
  }

  .panel-header h2 {
    font-size: 1.55rem;
  }

  .panel-header p {
    margin-top: 6px;
    font-size: 0.92rem;
  }

  .social-login {
    grid-template-columns: 1fr;
    gap: 8px;
  }

  .social-btn,
  .submit-btn,
  .field {
    min-height: 44px;
  }

  .divider {
    margin: 16px 0;
  }

  .signin-form {
    gap: 8px;
  }

  .label-row {
    margin-top: 4px;
  }

  .signup-link {
    margin-top: 16px;
  }
}
</style>
